import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/landing-page/Header';
import Footer from './components/landing-page/Footer';
import Home from './components/landing-page/pages/Home';
import Features from './components/landing-page/pages/Features';
import RequestDemo from './components/landing-page/pages/RequestDemo';
import SignIn from './components/landing-page/pages/SignIn';
import GetStarted from './components/landing-page/pages/GetStarted';
import PaymentInfo from './components/landing-page/pages/PaymentInfo';
import Pricing from './components/landing-page/pages/Pricing';
import Reviews from './components/landing-page/pages/Review';
import BlogUpdate from './components/landing-page/bloggupdate/BlogUpdate';
import WelcomePage from './components/landing-page/loadingpage/WelcomePage';
import ThankYou from './components/landing-page/loadingpage/ThankYou';
import Dashboard from './components/dashboard/Dashboard';
import GoogleCallback from './components/oauth/GoogleCallback';
import socketManager from './utils/socket'; // Keep your existing import path
import NetworkStatus from './components/shared/NetworkStatus';
import './css/styles.css';

// Route configuration
const routes = [
  { 
    path: '/', 
    element: <Home />, 
    showHeaderFooter: true,
    meta: { title: 'Home', requiresAuth: false }
  },
  { 
    path: '/features', 
    element: <Features />, 
    showHeaderFooter: true,
    meta: { title: 'Features', requiresAuth: false }
  },
  { 
    path: '/pricing', 
    element: <Pricing />, 
    showHeaderFooter: true,
    meta: { title: 'Pricing', requiresAuth: false }
  },
  { 
    path: '/reviews', 
    element: <Reviews />, 
    showHeaderFooter: true,
    meta: { title: 'Reviews', requiresAuth: false }
  },
  {
    path: '/insights',
    element: <BlogUpdate />,
    showHeaderFooter: true,
    meta: { title: 'Insights - POSlah', requiresAuth: false }
  },
  { 
    path: '/request-demo', 
    element: <RequestDemo />, 
    showHeaderFooter: false,
    meta: { title: 'Request Demo', requiresAuth: false }
  },
  { 
    path: '/signin', 
    element: <SignIn />, 
    showHeaderFooter: false,
    meta: { title: 'Sign In', requiresAuth: false }
  },
  { 
    path: '/getstarted', 
    element: <GetStarted />, 
    showHeaderFooter: false,
    meta: { title: 'Get Started', requiresAuth: false }
  },
  { 
    path: '/payment-info', 
    element: <PaymentInfo />, 
    showHeaderFooter: false,
    meta: { title: 'Payment Information', requiresAuth: false }
  },
  { 
    path: '/welcome', 
    element: <WelcomePage />, 
    showHeaderFooter: false,
    meta: { title: 'Welcome', requiresAuth: false }
  },
  { 
    path: '/thankyou', 
    element: <ThankYou />, 
    showHeaderFooter: false,
    meta: { title: 'Thank You', requiresAuth: false }
  },
  { 
    path: '/auth/google/callback', 
    element: <GoogleCallback />, 
    showHeaderFooter: false,
    meta: { title: 'Google Authentication', requiresAuth: false }
  },
  {
    path: '/dashboard/*',
    element: <Dashboard />,
    showHeaderFooter: false,
    meta: { title: 'Dashboard', requiresAuth: true }
  }
];

function AppContent() {
  const location = useLocation();
  
  const currentRoute = routes.find(route => 
    route.path.endsWith('*') 
      ? location.pathname.startsWith(route.path.slice(0, -2))
      : route.path === location.pathname
  );
  
  const showHeaderFooter = currentRoute?.showHeaderFooter ?? true;

  document.title = currentRoute?.meta?.title 
    ? `POSlah! | ${currentRoute.meta.title}` 
    : 'POSlah!';

  // Initialize socket connection for dashboard routes
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (location.pathname.startsWith('/dashboard') && token) {
      console.log('🔌 Initializing socket connection for dashboard...');
      const socket = socketManager.connect();
      
      if (socket) {
        // Make socket globally available
        window.socket = socket;
        console.log('✅ Socket initialized and made globally available');
      }
      
      return () => {
        // Don't disconnect on route changes within dashboard
        // Only disconnect when leaving dashboard entirely
        if (!location.pathname.startsWith('/dashboard')) {
          socketManager.disconnect();
          window.socket = null;
        }
      };
    } else if (!location.pathname.startsWith('/dashboard') && window.socket) {
      // Disconnect when leaving dashboard
      socketManager.disconnect();
      window.socket = null;
    }
  }, [location.pathname]);

  return (
    <>
      {showHeaderFooter && <Header />}
      <Routes>
        {routes.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}
      </Routes>
      {showHeaderFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <NetworkStatus />
      <AppContent />
    </Router>
  );
}

export default App;